import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  Switch,
  Select,
  Space,
  Divider,
  Tag,
  Alert,
  Progress,
  Tabs,
  Row,
  Col,
  Checkbox,
  Tooltip,
  Descriptions,
} from "antd";
import {
  CloudServerOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  TeamOutlined,
  UserOutlined,
  SettingOutlined,
  LinkOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { formatDateTime } from '@/utils/datetime';
import Icons from '@/components/third-party/Icons';
import TableContainer from '@/components/TableContainer';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';

const ActiveDirectoryIntegration = () => {
  const { isDark } = useTheme();
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const formBgColor = isDark ? defaultColors.palette.gray[750] || defaultColors.palette.gray[700] : '#f8faff';
  const borderColor = isDark ? defaultColors.palette.gray[600] : '#e5e7eb';

  const [form] = Form.useForm();
  const [attrForm] = Form.useForm();
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [syncingUsers, setSyncingUsers] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [adEnabled, setAdEnabled] = useState(false);
  const [groupMappings, setGroupMappings] = useState([]);
  const [syncHistory, setSyncHistory] = useState([]);

  const defaultConfig = {
    enabled: false,
    ldap_server: "",
    ldap_port: 389,
    use_ssl: false,
    ssl_port: 636,
    base_dn: "",
    bind_dn: "",
    bind_password: "",
    user_search_base: "",
    user_filter: "(objectClass=person)",
    group_search_base: "",
    group_filter: "(objectClass=group)",
    attributes: {
      username: "sAMAccountName",
      email: "mail",
      first_name: "givenName",
      last_name: "sn",
      display_name: "displayName",
      department: "department",
    },
  };

  useEffect(() => {
    form.setFieldsValue(defaultConfig);
    attrForm.setFieldsValue({ attributes: defaultConfig.attributes });
    setAdEnabled(defaultConfig.enabled);
    loadSyncHistory();
  }, []);

  const loadSyncHistory = () => {
    setSyncHistory([
      { id: 1, sync_date: "2025-11-01 14:30:00", users_synced: 45, users_created: 3, users_updated: 42, status: "success", duration: "2.5s" },
      { id: 2, sync_date: "2025-10-31 14:30:00", users_synced: 42, users_created: 0, users_updated: 42, status: "success", duration: "1.8s" },
    ]);
  };

  const testConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);
    try {
      const values = await form.validateFields(["ldap_server", "ldap_port", "base_dn", "bind_dn", "bind_password"]);
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setConnectionStatus({ success: true, message: "Successfully connected to Active Directory", server_info: { server: values.ldap_server, port: values.ldap_port, base_dn: values.base_dn } });
      toast.success("AD connection successful!");
    } catch (error) {
      setConnectionStatus({ success: false, message: "Failed to connect to Active Directory", error: error.message || "Connection timeout" });
      toast.error("AD connection failed!");
    } finally {
      setTestingConnection(false);
    }
  };

  const syncUsers = async () => {
    setSyncingUsers(true);
    setSyncProgress(0);
    try {
      const interval = setInterval(() => {
        setSyncProgress((prev) => { if (prev >= 100) { clearInterval(interval); return 100; } return prev + 10; });
      }, 500);
      await new Promise((resolve) => setTimeout(resolve, 5000));
      clearInterval(interval);
      setSyncProgress(100);
      toast.success("Users synced successfully from Active Directory!");
      loadSyncHistory();
    } catch (error) {
      toast.error("Failed to sync users from AD");
    } finally {
      setSyncingUsers(false);
      setTimeout(() => setSyncProgress(0), 2000);
    }
  };

  const saveConfiguration = async () => {
    try {
      await form.validateFields();
      toast.success("AD configuration saved successfully!");
    } catch (error) {
      toast.error("Failed to save configuration");
    }
  };

  const saveAttributeMapping = async () => {
    try {
      await attrForm.validateFields();
      toast.success("Attribute mapping saved successfully!");
    } catch (error) {
      toast.error("Failed to save attribute mapping");
    }
  };

  const labelSpan = (text) => (
    <span className="font-medium" style={{ color: textColor }}>
      {text}
    </span>
  );

  const syncHistoryColumns = [
    { title: "Date/Time", dataIndex: "sync_date", key: "sync_date", width: "20%", render: (text) => formatDateTime(text, "DD-MMM-YYYY HH:mm:ss") },
    { title: "Users Synced", dataIndex: "users_synced", key: "users_synced", align: "center", width: "15%" },
    { title: "Created", dataIndex: "users_created", key: "users_created", align: "center", width: "12%", render: (count) => <Tag color="green">{count > 0 ? `+${count}` : count}</Tag> },
    { title: "Updated", dataIndex: "users_updated", key: "users_updated", align: "center", width: "12%", render: (count) => <Tag color="blue">{count}</Tag> },
    { title: "Duration", dataIndex: "duration", key: "duration", align: "center", width: "12%" },
    {
      title: "Status", dataIndex: "status", key: "status", align: "center", width: "15%",
      render: (status) => (
        <Tag icon={status === "success" ? <CheckCircleOutlined /> : <CloseCircleOutlined />} color={status === "success" ? "success" : "error"}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
  ];

  return (
    <Card
      className="border border-gray-300 shadow-md"
      extra={
        <Space>
          <span style={{ color: textColor, fontSize: 13 }}>AD Authentication:</span>
          <Switch
            checked={adEnabled}
            onChange={setAdEnabled}
            checkedChildren="Enabled"
            unCheckedChildren="Disabled"
          />
        </Space>
      }
    >
      {adEnabled && (
        <Alert
          message="Active Directory authentication is enabled"
          description="Users can now log in using their Active Directory credentials. Local authentication is still available as fallback."
          type="success"
          showIcon
          icon={<LockOutlined />}
          style={{ marginBottom: 16 }}
        />
      )}

      <Tabs defaultActiveKey="connection" size="large">

        {/* ── Connection Settings ── */}
        <Tabs.TabPane tab={<span><LinkOutlined /> Connection Settings</span>} key="connection">
          <Form form={form} layout="vertical" initialValues={defaultConfig}>

            {/* Server block */}
            <div
              className="rounded-xl border mb-5"
              style={{ backgroundColor: formBgColor, borderColor: '#3b82f6', borderLeftWidth: 4, borderLeftStyle: 'solid', padding: '20px 24px 12px' }}
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="p-1.5 rounded-md" style={{ backgroundColor: isDark ? 'rgba(59,130,246,0.08)' : '#eff6ff' }}>
                  <Icons.Server className="w-4 h-4" style={{ color: '#3b82f6' }} />
                </div>
                <span className="text-sm font-semibold" style={{ color: '#3b82f6' }}>Server Connection</span>
              </div>
              <Row gutter={[16, 0]} align="bottom">
                <Col xs={24} md={10}>
                  <Form.Item
                    name="ldap_server"
                    label={labelSpan('LDAP Server')}
                    rules={[{ required: true, message: "Please enter LDAP server" }]}
                  >
                    <Input size="large" placeholder="ldap.company.com or IP address" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={4}>
                  <Form.Item
                    name="ldap_port"
                    label={labelSpan('Port')}
                    rules={[{ required: true, message: "Please enter port" }]}
                  >
                    <InputNumber size="large" min={1} max={65535} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={4}>
                  <Form.Item name="use_ssl" valuePropName="checked" label=" " colon={false}>
                    <Checkbox>Use SSL/TLS (Port 636)</Checkbox>
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item
                    name="base_dn"
                    label={labelSpan('Base DN')}
                    rules={[{ required: true, message: "Please enter Base DN" }]}
                  >
                    <Input size="large" placeholder="DC=company,DC=com" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="bind_dn"
                    label={labelSpan('Bind DN (Service Account)')}
                    rules={[{ required: true, message: "Please enter Bind DN" }]}
                  >
                    <Input size="large" placeholder="CN=ldap-service,OU=Service Accounts,DC=company,DC=com" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item
                    name="bind_password"
                    label={labelSpan('Bind Password')}
                    rules={[{ required: true, message: "Please enter password" }]}
                  >
                    <Input.Password size="large" placeholder="Service account password" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={4}>
                  <Form.Item label=" " colon={false}>
                    <div className="flex gap-2">
                      <Tooltip title="Reset">
                        <Button icon={<Icons.Undo2 className="w-4 h-4" />} onClick={() => form.resetFields()} />
                      </Tooltip>
                      <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        onClick={testConnection}
                        loading={testingConnection}
                        className="flex-1"
                      >
                        Test
                      </Button>
                      <Button
                        icon={<SettingOutlined />}
                        onClick={saveConfiguration}
                      >
                        Save
                      </Button>
                    </div>
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Search Configuration block */}
            <div
              className="rounded-xl border mb-5"
              style={{ backgroundColor: formBgColor, borderColor: '#8b5cf6', borderLeftWidth: 4, borderLeftStyle: 'solid', padding: '20px 24px 12px' }}
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="p-1.5 rounded-md" style={{ backgroundColor: isDark ? 'rgba(139,92,246,0.08)' : '#f5f3ff' }}>
                  <Icons.SlidersHorizontal className="w-4 h-4" style={{ color: '#8b5cf6' }} />
                </div>
                <span className="text-sm font-semibold" style={{ color: '#8b5cf6' }}>Search Configuration</span>
              </div>
              <Row gutter={[16, 0]}>
                <Col xs={24} md={6}>
                  <Form.Item name="user_search_base" label={labelSpan('User Search Base')}>
                    <Input size="large" placeholder="OU=Users,DC=company,DC=com" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item name="user_filter" label={labelSpan('User Filter')}>
                    <Input size="large" placeholder="(objectClass=person)" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item name="group_search_base" label={labelSpan('Group Search Base')}>
                    <Input size="large" placeholder="OU=Groups,DC=company,DC=com" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item name="group_filter" label={labelSpan('Group Filter')}>
                    <Input size="large" placeholder="(objectClass=group)" />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {connectionStatus && (
              <Alert
                message={connectionStatus.success ? "Connection Successful" : "Connection Failed"}
                description={
                  connectionStatus.success ? (
                    <Descriptions size="small" column={1}>
                      <Descriptions.Item label="Server">{connectionStatus.server_info.server}</Descriptions.Item>
                      <Descriptions.Item label="Port">{connectionStatus.server_info.port}</Descriptions.Item>
                      <Descriptions.Item label="Base DN">{connectionStatus.server_info.base_dn}</Descriptions.Item>
                    </Descriptions>
                  ) : (
                    <div>
                      <div>{connectionStatus.message}</div>
                      {connectionStatus.error && <div style={{ marginTop: 8, color: "#ff4d4f" }}>Error: {connectionStatus.error}</div>}
                    </div>
                  )
                }
                type={connectionStatus.success ? "success" : "error"}
                showIcon
                style={{ marginTop: 8 }}
                closable
              />
            )}
          </Form>
        </Tabs.TabPane>

        {/* ── User Sync ── */}
        <Tabs.TabPane tab={<span><SyncOutlined /> User Sync</span>} key="sync">
          <div
            className="rounded-xl border mb-5"
            style={{ backgroundColor: formBgColor, borderColor: '#10b981', borderLeftWidth: 4, borderLeftStyle: 'solid', padding: '20px 24px 12px' }}
          >
            <div className="flex items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-md" style={{ backgroundColor: isDark ? 'rgba(16,185,129,0.08)' : '#ecfdf5' }}>
                  <Icons.Repeat className="w-4 h-4" style={{ color: '#10b981' }} />
                </div>
                <span className="text-sm font-semibold" style={{ color: '#10b981' }}>Sync Users from Active Directory</span>
              </div>
              <Button
                type="primary"
                icon={<SyncOutlined />}
                onClick={syncUsers}
                loading={syncingUsers}
                disabled={!connectionStatus?.success}
                style={{ backgroundColor: '#10b981', borderColor: '#10b981' }}
              >
                Sync Now
              </Button>
            </div>

            {syncingUsers && (
              <div style={{ marginBottom: 16 }}>
                <Progress percent={syncProgress} status="active" />
                <div style={{ textAlign: "center", marginTop: 8, color: textColor }}>Syncing users from Active Directory...</div>
              </div>
            )}

            {!connectionStatus?.success && (
              <Alert
                message="Connection Required"
                description="Please test and verify the AD connection in the 'Connection Settings' tab before syncing users."
                type="warning"
                showIcon
                style={{ marginBottom: 8 }}
              />
            )}
          </div>

          <Divider className="my-2" />
          <TableContainer
            className="ad-sync-history-table"
            data={syncHistory}
            columns={syncHistoryColumns}
            rowKey="id"
            pagination={{ pageSize: 10, showSizeChanger: false, showTotal: undefined }}
            size="small"
            bordered
            height="calc(100vh - 595px)"
          />
          {/* Page-specific pagination-bar spacing — not something TableContainer covers generically. */}
          <style>
            {`
              .ad-sync-history-table .ant-table-pagination {
                margin-top: 16px !important;
              }
            `}
          </style>
        </Tabs.TabPane>

        {/* ── Group Mapping ── */}
        <Tabs.TabPane tab={<span><TeamOutlined /> Group Mapping</span>} key="mapping">
          <div
            className="rounded-xl border mb-5"
            style={{ backgroundColor: formBgColor, borderColor: '#f59e0b', borderLeftWidth: 4, borderLeftStyle: 'solid', padding: '20px 24px 12px' }}
          >
            <div className="flex items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-md" style={{ backgroundColor: isDark ? 'rgba(245,158,11,0.08)' : '#fffbeb' }}>
                  <Icons.GitBranch className="w-4 h-4" style={{ color: '#f59e0b' }} />
                </div>
                <span className="text-sm font-semibold" style={{ color: '#f59e0b' }}>Map AD Groups to Application Roles</span>
              </div>
              <Button type="primary" icon={<TeamOutlined />} style={{ backgroundColor: '#f59e0b', borderColor: '#f59e0b' }}>
                Add Mapping
              </Button>
            </div>
            <Alert
              message="Map Active Directory groups to application roles. Users in mapped AD groups will automatically receive the corresponding application role."
              type="info"
              showIcon
              style={{ marginBottom: 8 }}
            />
          </div>

          <TableContainer
            className="ad-group-mapping-table"
            data={groupMappings}
            columns={[
              { title: "AD Group", dataIndex: "ad_group", key: "ad_group" },
              { title: "Application Role", dataIndex: "app_role", key: "app_role", render: (role) => <Tag color="blue">{role}</Tag> },
              { title: "Actions", key: "actions", render: () => <Button type="link" danger size="small">Remove</Button> },
            ]}
            size="small"
            bordered
            height="calc(100vh - 595px)"
          />
        </Tabs.TabPane>

        {/* ── Attribute Mapping ── */}
        <Tabs.TabPane tab={<span><UserOutlined /> Attribute Mapping</span>} key="attributes">
          <div
            className="rounded-xl border mb-5"
            style={{ backgroundColor: formBgColor, borderColor: '#3b82f6', borderLeftWidth: 4, borderLeftStyle: 'solid', padding: '20px 24px 12px' }}
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="p-1.5 rounded-md" style={{ backgroundColor: isDark ? 'rgba(59,130,246,0.08)' : '#eff6ff' }}>
                <Icons.User className="w-4 h-4" style={{ color: '#3b82f6' }} />
              </div>
              <span className="text-sm font-semibold" style={{ color: '#3b82f6' }}>Map AD Attributes to User Fields</span>
            </div>
            <Alert
              message="Configure which Active Directory attributes map to application user fields."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Form form={attrForm} layout="vertical">
              <Row gutter={[16, 0]}>
                <Col xs={24} md={4}>
                  <Form.Item name={["attributes", "username"]} label={labelSpan('Username')}>
                    <Input size="large" placeholder="sAMAccountName" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={4}>
                  <Form.Item name={["attributes", "email"]} label={labelSpan('Email')}>
                    <Input size="large" placeholder="mail" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={4}>
                  <Form.Item name={["attributes", "first_name"]} label={labelSpan('First Name')}>
                    <Input size="large" placeholder="givenName" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={4}>
                  <Form.Item name={["attributes", "last_name"]} label={labelSpan('Last Name')}>
                    <Input size="large" placeholder="sn" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={4}>
                  <Form.Item name={["attributes", "display_name"]} label={labelSpan('Display Name')}>
                    <Input size="large" placeholder="displayName" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={4}>
                  <Form.Item name={["attributes", "department"]} label={labelSpan('Department')}>
                    <Input size="large" placeholder="department" />
                  </Form.Item>
                </Col>
              </Row>
              <div className="flex justify-end mt-2">
                <Button type="primary" icon={<Icons.Check className="w-4 h-4" />} onClick={saveAttributeMapping}>
                  Save Attribute Mapping
                </Button>
              </div>
            </Form>
          </div>
        </Tabs.TabPane>

      </Tabs>
    </Card>
  );
};

export default ActiveDirectoryIntegration;
